import * as axios from "./Axios";

const FUNNEL_STAGE_META = [
  { key: "generated", name: "Generated", color: "#7C73E6" },
  { key: "opened", name: "Opened", color: "#A688FA" },
  { key: "actions", name: "Actions", color: "#14B8A6" },
  { key: "paid", name: "Paid", color: "#374151" },
];

const FUNNEL_KPI_META = [
  {
    key: "links_generated",
    title: "Links Generated",
    icon: "EyeIcon",
    color: "#6366F1",
    altKeys: ["linksGenerated", "generated"],
  },
  {
    key: "opened",
    title: "Opened",
    icon: "ExternalLinkIcon",
    color: "#8B5CF6",
    altKeys: ["opens", "opened_links"],
  },
  {
    key: "pay_now_clicks",
    title: "Pay Now Clicks",
    icon: "CreditCardIcon",
    color: "#6366F1",
    altKeys: ["payNowClicks", "pay_now", "actions_taken", "actionsTaken", "actions"],
  },
  {
    key: "payments",
    title: "Payments",
    icon: "CheckCircleIcon",
    color: "#16A34A",
    altKeys: ["paid", "payments_count"],
  },
  {
    key: "total_revenue",
    title: "Total Revenue",
    icon: "DollarSignIcon",
    color: "#6366F1",
    altKeys: ["totalRevenue", "revenue", "revenue_collected"],
    currency: true,
    highlight: true,
  },
];

const CONVERSION_RATE_META = [
  { key: "open_rate", label: "Open rate", color: "#8B5CF6", altKeys: ["openRate"] },
  { key: "action_rate", label: "Action rate", color: "#0FB9A3D9", altKeys: ["actionRate"] },
  { key: "pay_rate", label: "Pay rate", color: "#6366F1D9", altKeys: ["payRate"] },
];

const ACTIVITY_KPI_META = [
  { key: "links_generated", title: "Links Generated", icon: "EyeIcon", color: "#6366F1", altKeys: ["linksGenerated", "generated"] },
  { key: "download_clicks", title: "Download Clicks", icon: "DownloadIcon", color: "#00CFE8", altKeys: ["downloadClicks", "download"] },
  { key: "pay_now_clicks", title: "PayNow Clicks", icon: "CreditCardIcon", color: "#6366F1", altKeys: ["payNowClicks", "pay_now"] },
  { key: "tracking_clicks", title: "Tracking Clicks", icon: "NavigationIcon", color: "#28C76F", altKeys: ["trackingClicks", "tracking"] },
  { key: "edit_clicks", title: "Edit Clicks", icon: "EditIcon", color: "#6366F1", altKeys: ["editClicks", "edit"] },
  { key: "customers_opened_links", title: "Customers Opened Links", icon: "ExternalLinkIcon", color: "#00CFE8", altKeys: ["customersOpenedLinks", "opened_link", "opened"] },
  { key: "customer_paid_via_link", title: "Customer Paid via link", icon: "CheckCircleIcon", color: "#28C76F", altKeys: ["customerPaidViaLink", "paid_bookings", "paid"] },
  {
    key: "total_revenue_generated",
    title: "Total Revenue generated",
    icon: "DollarSignIcon",
    color: "#6366F1",
    highlight: true,
    currency: true,
    altKeys: ["totalRevenueGenerated", "total_revenue", "revenue"],
  },
];

const ACTION_MIX_META = [
  { key: "pay", name: "Pay", color: "#6366F1", altKeys: ["Pay", "pay_now"] },
  { key: "track", name: "Track", color: "#0FB9A3", altKeys: ["Track", "tracking"] },
  { key: "download", name: "Download", color: "#8B5CF6", altKeys: ["Download"] },
  { key: "edit", name: "Edit", color: "#F59E0B", altKeys: ["Edit"] },
];

const pickValue = (source, keys, fallback = 0) => {
  if (!source || typeof source !== "object") return fallback;

  for (let i = 0; i < keys.length; i += 1) {
    const key = keys[i];
    if (source[key] !== undefined && source[key] !== null) {
      return source[key];
    }
  }

  return fallback;
};

const unwrapPayload = (response) => {
  const root = response && response.data !== undefined ? response.data : response;
  if (!root || typeof root !== "object") return {};
  if (root.data && typeof root.data === "object" && !Array.isArray(root.data)) {
    return root.data;
  }
  if (root.result && typeof root.result === "object") {
    return root.result;
  }
  return root;
};

export const formatNumber = (value) =>
  Number(value || 0).toLocaleString("en-GB");

export const formatCurrency = (value) =>
  new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
    maximumFractionDigits: 0,
  }).format(Number(value || 0));

const formatMetricValue = (value, currency = false) => {
  if (typeof value === "string" && value.trim() !== "") return value;
  return currency ? formatCurrency(value) : formatNumber(value);
};

const toPercentNumber = (value) => {
  if (typeof value === "string") {
    return Number(String(value).replace("%", "")) || 0;
  }
  return Number(value || 0);
};

const buildKpisFromObject = (source, meta) =>
  meta.map((item) => ({
    title: item.title,
    value: formatMetricValue(
      pickValue(source, [item.key, ...(item.altKeys || [])], 0),
      item.currency
    ),
    icon: item.icon,
    color: item.color,
    highlight: !!item.highlight,
  }));

const enrichKpiArray = (kpis, meta) => {
  if (!Array.isArray(kpis)) return null;

  return kpis.map((kpi, index) => {
    const fallback = meta[index] || {};
    const title = kpi.title || kpi.label || kpi.name || fallback.title || `Metric ${index + 1}`;
    const normalizedTitle = String(title).toLowerCase().replace(/\s+/g, " ").trim();
    const matched = meta.find((item) => {
      const itemTitle = String(item.title).toLowerCase();
      return itemTitle === normalizedTitle
        || (item.key && item.key.replace(/_/g, " ") === normalizedTitle)
        || (item.altKeys || []).some((alt) => String(alt).replace(/_/g, " ").toLowerCase() === normalizedTitle);
    }) || fallback;

    return {
      title: matched.title || title,
      value: formatMetricValue(
        kpi.value !== undefined ? kpi.value : kpi.count,
        matched.currency || /revenue/i.test(title)
      ),
      icon: kpi.icon || matched.icon,
      color: kpi.color || matched.color,
      highlight: kpi.highlight !== undefined ? !!kpi.highlight : !!matched.highlight,
    };
  });
};

export const emptyFunnelData = () => ({
  kpis: FUNNEL_KPI_META.map((item) => ({
    title: item.title,
    value: item.currency ? formatCurrency(0) : formatNumber(0),
    icon: item.icon,
    color: item.color,
    highlight: !!item.highlight,
  })),
  funnel: FUNNEL_STAGE_META.map((stage) => ({
    name: stage.name,
    value: 0,
    percent: stage.key === "generated" ? 100 : 0,
    color: stage.color,
  })),
  revenueCollected: formatCurrency(0),
  conversionRates: CONVERSION_RATE_META.map((rate) => ({
    label: rate.label,
    value: 0,
    color: rate.color,
  })),
});

const WEEKDAY_CATEGORIES = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const toNumberSeries = (values, length = 7) => {
  const source = Array.isArray(values) ? values : [];
  return Array.from({ length }, (_, index) => Number(source[index] || 0));
};

const normalizeDailyTrend = (trend = {}) => {
  const categories = Array.isArray(trend.categories) && trend.categories.length === 7
    ? trend.categories
    : WEEKDAY_CATEGORIES;

  return {
    categories,
    opens: toNumberSeries(trend.opens || trend.open),
    payNowClicks: toNumberSeries(
      trend.pay_now_clicks || trend.payNowClicks || trend.clicks || trend.click
    ),
    payments: toNumberSeries(trend.payments || trend.payment),
  };
};

export const emptyActivityData = () => ({
  kpis: ACTIVITY_KPI_META.map((item) => ({
    title: item.title,
    value: item.currency ? formatCurrency(0) : formatNumber(0),
    icon: item.icon,
    color: item.color,
    highlight: !!item.highlight,
  })),
  dailyTrend: normalizeDailyTrend(),
  actionMix: ACTION_MIX_META.map((item) => ({
    name: item.name,
    value: 0,
    color: item.color,
  })),
  totalActions: 0,
  businessMetrics: [
    {
      title: "BHX Uplift Eligible",
      value: formatNumber(0),
      subtext: "customers eligible for upgrade",
      icon: "TrendingUpIcon",
    },
    {
      title: "BHX Executive Chosen",
      value: formatNumber(0),
      subtext: "executive upgrades selected",
      icon: "AwardIcon",
    },
    {
      title: "Upsell Conversion",
      value: "0%",
      subtext: "0 of 0 eligible converted",
      icon: "PieChartIcon",
      progress: 0,
    },
  ],
});

export const normalizeFunnelData = (response) => {
  const payload = unwrapPayload(response);
  const base = emptyFunnelData();

  const kpiSource = payload.kpis || payload.summary || payload;
  const enrichedKpis = enrichKpiArray(payload.kpis, FUNNEL_KPI_META);
  base.kpis = enrichedKpis || buildKpisFromObject(kpiSource, FUNNEL_KPI_META);

  const funnelSource = payload.funnel || payload.stages || payload.funnel_stages || {};
  if (Array.isArray(funnelSource) && funnelSource.length) {
    base.funnel = funnelSource.map((stage, index) => {
      const meta = FUNNEL_STAGE_META[index] || FUNNEL_STAGE_META[0];
      const value = Number(stage.value !== undefined ? stage.value : stage.count || 0);
      const percent = stage.percent !== undefined
        ? toPercentNumber(stage.percent)
        : (index === 0 ? 100 : 0);

      return {
        name: stage.name || stage.label || meta.name,
        value,
        percent,
        color: stage.color || meta.color,
      };
    });
  } else {
    const generated = Number(
      pickValue(funnelSource, ["generated", "links_generated"], base.kpis[0]
        ? Number(String(base.kpis[0].value).replace(/,/g, ""))
        : 0)
    );

    base.funnel = FUNNEL_STAGE_META.map((stage) => {
      const value = Number(pickValue(funnelSource, [stage.key], stage.key === "generated" ? generated : 0));
      const percent = generated > 0 ? Number(((value / generated) * 100).toFixed(1)) : 0;

      return {
        name: stage.name,
        value,
        percent: stage.key === "generated" ? 100 : percent,
        color: stage.color,
      };
    });
  }

  const ratesSource = payload.conversionRates || payload.conversion_rates || payload.rates || {};
  if (Array.isArray(ratesSource) && ratesSource.length) {
    base.conversionRates = ratesSource.map((rate, index) => {
      const meta = CONVERSION_RATE_META[index] || CONVERSION_RATE_META[0];
      return {
        label: rate.label || rate.name || meta.label,
        value: toPercentNumber(rate.value !== undefined ? rate.value : rate.rate),
        color: rate.color || meta.color,
      };
    });
  } else {
    base.conversionRates = CONVERSION_RATE_META.map((rate) => ({
      label: rate.label,
      value: toPercentNumber(pickValue(ratesSource, [rate.key, ...(rate.altKeys || [])], 0)),
      color: rate.color,
    }));
  }

  const revenue = pickValue(
    payload,
    ["revenueCollected", "revenue_collected", "total_revenue", "revenue"],
    base.kpis.find((k) => /revenue/i.test(k.title))
      ? String(base.kpis.find((k) => /revenue/i.test(k.title)).value).replace(/[£,]/g, "")
      : 0
  );
  base.revenueCollected = formatMetricValue(revenue, true);

  return base;
};

export const normalizeActivityData = (response) => {
  const payload = unwrapPayload(response);
  const base = emptyActivityData();

  const kpiSource = payload.kpis || payload.summary || payload.button_counts || payload;
  const enrichedKpis = enrichKpiArray(payload.kpis, ACTIVITY_KPI_META);
  base.kpis = enrichedKpis || buildKpisFromObject(kpiSource, ACTIVITY_KPI_META);

  const trend = payload.dailyTrend || payload.daily_trend || payload.trend || {};
  base.dailyTrend = normalizeDailyTrend(trend);

  const mixSource = payload.actionMix || payload.action_mix || payload.actions || {};
  if (Array.isArray(mixSource) && mixSource.length) {
    base.actionMix = mixSource.map((item, index) => {
      const meta = ACTION_MIX_META[index] || ACTION_MIX_META[0];
      return {
        name: item.name || item.label || meta.name,
        value: Number(item.value !== undefined ? item.value : item.count || 0),
        color: item.color || meta.color,
      };
    });
  } else {
    base.actionMix = ACTION_MIX_META.map((item) => ({
      name: item.name,
      value: Number(pickValue(mixSource, [item.key, ...(item.altKeys || [])], 0)),
      color: item.color,
    }));
  }

  base.totalActions = Number(
    pickValue(
      payload,
      ["totalActions", "total_actions"],
      base.actionMix.reduce((sum, item) => sum + Number(item.value || 0), 0)
    )
  );

  const metricsSource = payload.businessMetrics || payload.business_metrics || {};
  if (Array.isArray(metricsSource) && metricsSource.length) {
    base.businessMetrics = metricsSource.map((metric, index) => {
      const fallback = base.businessMetrics[index] || {};
      return {
        title: metric.title || metric.label || fallback.title,
        value: metric.value !== undefined ? String(metric.value) : fallback.value,
        subtext: metric.subtext || metric.description || fallback.subtext,
        icon: metric.icon || fallback.icon,
        progress: metric.progress !== undefined ? Number(metric.progress) : fallback.progress,
      };
    });
  } else {
    const eligible = Number(
      pickValue(metricsSource, ["bhx_uplift_eligible", "bhxUpliftEligible", "eligible"], 0)
    );
    const chosen = Number(
      pickValue(metricsSource, ["bhx_executive_chosen", "bhxExecutiveChosen", "chosen"], 0)
    );
    const conversion = toPercentNumber(
      pickValue(metricsSource, ["upsell_conversion", "upsellConversion", "conversion"], 0)
    );

    base.businessMetrics = [
      {
        title: "BHX Uplift Eligible",
        value: formatNumber(eligible),
        subtext: "customers eligible for upgrade",
        icon: "TrendingUpIcon",
      },
      {
        title: "BHX Executive Chosen",
        value: formatNumber(chosen),
        subtext: "executive upgrades selected",
        icon: "AwardIcon",
      },
      {
        title: "Upsell Conversion",
        value: `${conversion}%`,
        subtext: `${formatNumber(chosen)} of ${formatNumber(eligible)} eligible converted`,
        icon: "PieChartIcon",
        progress: conversion,
      },
    ];
  }

  return base;
};

export const buildJourneyAnalyticsPayload = ({ dateFrom, dateTo }) => ({
  startDate: dateFrom,
  endDate: dateTo,
});

export const fetchFunnelAnalytics = (data) =>
  axios.post("admin/journey-analytics/funnel", data);

export const fetchActivityAnalytics = (data) =>
  axios.post("admin/journey-analytics/activity", data);

export const downloadJourneyAnalytics = (data) =>
  axios.post("admin/download-journey-analytics", data);
